import api from '@/lib/axios';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export interface AssignmentFile {
  id: string;
  fileUrl: string;
  fileName: string | null;
  createdAt: string;
}

export interface AssignmentListRow {
  id: string;
  sectionId: string;
  sectionName: string;
  className: string;
  classSubjectId: string;
  subjectName: string;
  name: string;
  instructions: string | null;
  dueDate: string;
  points: number | null;
  resubmission: boolean;
  extraDaysForResubmission: number | null;
  createdByName: string | null;
  fileCount: number;
  submissionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssignmentDetail extends AssignmentListRow {
  files: AssignmentFile[];
}

export interface CreateAssignmentData {
  sectionId: string;
  classSubjectId: string;
  name: string;
  instructions?: string;
  dueDate: string;
  points?: number;
  resubmission?: boolean;
  extraDaysForResubmission?: number;
}

export type UpdateAssignmentData = Partial<CreateAssignmentData>;

export interface CreateAssignmentFileData {
  fileUrl: string;
  fileName?: string;
}

export interface AssignmentListParams {
  sectionId?: string;
  classSubjectId?: string;
  academicYearId?: string;
}

export interface AssignmentSubmissionRow {
  id: string;
  assignmentId: string;
  assignmentName: string;
  studentId: string;
  studentName: string | null;
  admissionNo: string | null;
  status: number;
  points: number | null;
  feedback: string | null;
  files: AssignmentFile[];
  createdAt: string;
  updatedAt: string;
}

export interface GradeSubmissionData {
  status: 'accept' | 'reject';
  points?: number;
  feedback?: string;
}

export interface AssignmentReport {
  assignmentId: string;
  total: number;
  submitted: number;
  unsubmitted: number;
  totalPoints: number;
  obtainedPoints: number;
  percentage: number;
}

export const AssignmentsApi = {
  list: async (params?: AssignmentListParams): Promise<AssignmentListRow[]> =>
    unwrap(await api.get('/school/assignments', { params })),

  get: async (id: string): Promise<AssignmentDetail> => unwrap(await api.get(`/school/assignments/${id}`)),

  create: async (data: CreateAssignmentData): Promise<AssignmentListRow> => unwrap(await api.post('/school/assignments', data)),

  update: async (id: string, data: UpdateAssignmentData): Promise<AssignmentListRow> =>
    unwrap(await api.patch(`/school/assignments/${id}`, data)),

  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/school/assignments/${id}`)),

  addFile: async (id: string, data: CreateAssignmentFileData): Promise<AssignmentFile> =>
    unwrap(await api.post(`/school/assignments/${id}/files`, data)),

  removeFile: async (id: string, fileId: string): Promise<void> =>
    unwrap(await api.delete(`/school/assignments/${id}/files/${fileId}`)),

  report: async (assignmentId: string): Promise<AssignmentReport> =>
    unwrap(await api.get('/school/assignments/report', { params: { assignmentId } })),

  submissions: async (assignmentId: string): Promise<AssignmentSubmissionRow[]> =>
    unwrap(await api.get('/school/assignment-submissions', { params: { assignmentId } })),

  gradeSubmission: async (submissionId: string, data: GradeSubmissionData) =>
    unwrap(await api.patch(`/school/assignment-submissions/${submissionId}`, data)),
};
